import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  getAgeGroups,
  createAgeGroup,
  updateAgeGroup,
  deleteAgeGroup,
  type AgeGroup,
} from "../../services/api";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";

type FormState = {
  name: string;
  min_age: string;
  max_age: string;
  display_order: string;
  is_active: boolean;
  default_difficulty: "" | "easy" | "medium" | "hard";
};

const EMPTY_FORM: FormState = {
  name: "",
  min_age: "",
  max_age: "",
  display_order: "",
  is_active: true,
  default_difficulty: "",
};

// Localized label for a quiz difficulty value.
function difficultyLabel(
  d: "easy" | "medium" | "hard" | null | undefined,
  t: (key: string, fallback: string) => string
): string {
  if (d === "easy") return t("ageGroups.diff_easy", "Ușor");
  if (d === "medium") return t("ageGroups.diff_medium", "Mediu");
  if (d === "hard") return t("ageGroups.diff_hard", "Greu");
  return t("ageGroups.diff_none", "Nesetat");
}

// Human-readable summary of an age group's bounds.
function describeBounds(g: AgeGroup, t: (key: string, fallback: string) => string): string {
  if (g.min_age == null && g.max_age == null) return t("ageGroups.bounds_any", "Fără limită");
  if (g.min_age == null && g.max_age != null) return `${t("ageGroups.under", "Sub")} ${g.max_age + 1} (${g.max_age} ${t("ageGroups.and_below", "și mai mic")})`;
  if (g.min_age != null && g.max_age == null) return `${g.min_age}+ ${t("ageGroups.years", "ani")}`;
  return `${g.min_age}–${g.max_age} ${t("ageGroups.years", "ani")}`;
}

export default function AgeGroupsAdmin() {
  const { t } = useTranslation();

  const [rows, setRows] = useState<AgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AgeGroup | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getAgeGroups({ all: true });
      setRows(data);
    } catch (err) {
      console.error("Failed to load age groups", err);
      toast.error(t("ageGroups.err_load", "Nu s-au putut încărca grupele de vârstă"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (g: AgeGroup) => {
    setEditing(g);
    setForm({
      name: g.name,
      min_age: g.min_age != null ? String(g.min_age) : "",
      max_age: g.max_age != null ? String(g.max_age) : "",
      display_order: String(g.display_order ?? ""),
      is_active: g.is_active,
      default_difficulty: g.default_difficulty ?? "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const parseAge = (s: string): number | null | undefined => {
    if (s.trim() === "") return null;
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0 || n > 99) return undefined;
    return Math.floor(n);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("ageGroups.err_name_required", "Numele este obligatoriu"));
      return;
    }

    const min = parseAge(form.min_age);
    const max = parseAge(form.max_age);
    if (min === undefined) {
      toast.error(t("ageGroups.err_min_invalid", "Vârsta minimă trebuie să fie între 0 și 99"));
      return;
    }
    if (max === undefined) {
      toast.error(t("ageGroups.err_max_invalid", "Vârsta maximă trebuie să fie între 0 și 99"));
      return;
    }
    if (min != null && max != null && min > max) {
      toast.error(t("ageGroups.err_min_gt_max", "Vârsta minimă nu poate fi mai mare decât cea maximă"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        min_age: min,
        max_age: max,
        display_order: form.display_order.trim() === "" ? undefined : Number(form.display_order),
        is_active: form.is_active,
        default_difficulty: form.default_difficulty === "" ? null : form.default_difficulty,
      };
      if (editing) {
        await updateAgeGroup(editing.id, payload);
        toast.success(t("ageGroups.updated", "Grupă actualizată"));
      } else {
        await createAgeGroup(payload);
        toast.success(t("ageGroups.created", "Grupă creată"));
      }
      await load();
      closeModal();
    } catch (err: any) {
      console.error("Failed to save age group", err);
      toast.error(err.response?.data?.error || t("ageGroups.err_save", "Nu s-a putut salva grupa"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (g: AgeGroup) => {
    if (!window.confirm(t("ageGroups.confirm_delete", "Ștergi grupa '{{name}}'?").replace("{{name}}", g.name))) return;
    try {
      await deleteAgeGroup(g.id);
      toast.success(t("ageGroups.deleted", "Grupă ștearsă"));
      await load();
    } catch (err: any) {
      console.error("Failed to delete age group", err);
      toast.error(err.response?.data?.error || t("ageGroups.err_delete", "Nu s-a putut șterge grupa"));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        <AdminPageHeader
          eyebrow={t("sidebar.contentEducation", "Conținut și Educație")}
          title={t("ageGroups.title", "Grupe de vârstă")}
          subtitle={t("ageGroups.description", "Definește grupele de vârstă disponibile pentru toate cluburile. Cluburile selectează din această listă la crearea echipelor.")}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={openCreate}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              {t("ageGroups.add", "Adaugă grupă")}
            </AdminButton>
          }
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("ageGroups.col_order", "Ordine")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("ageGroups.col_name", "Nume")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("ageGroups.col_bounds", "Interval")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("ageGroups.col_guardian", "Părinte obligatoriu")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("ageGroups.col_difficulty", "Dificultate quiz")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("ageGroups.col_status", "Status")}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {t("ageGroups.col_actions", "Acțiuni")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((g) => {
                  const guardianRequired = g.max_age != null && g.max_age < 14;
                  return (
                    <tr key={g.id} className={g.is_active ? "" : "opacity-50"}>
                      <td className="px-4 py-3 text-sm text-slate-500">{g.display_order}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800">{g.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{describeBounds(g, t)}</td>
                      <td className="px-4 py-3 text-sm">
                        {guardianRequired ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700 font-medium">
                            {t("ageGroups.yes", "Da")}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">{t("ageGroups.no", "Nu")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {g.default_difficulty ? (
                          <span
                            className={
                              "px-2 py-1 rounded-full text-xs font-medium " +
                              (g.default_difficulty === "easy"
                                ? "bg-emerald-100 text-emerald-700"
                                : g.default_difficulty === "medium"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-rose-100 text-rose-700")
                            }
                          >
                            {difficultyLabel(g.default_difficulty, t)}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">{t("ageGroups.diff_none", "Nesetat")}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {g.is_active ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700 font-medium">
                            {t("ageGroups.active", "Activă")}
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-600 font-medium">
                            {t("ageGroups.inactive", "Inactivă")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm space-x-2">
                        <button
                          onClick={() => openEdit(g)}
                          className="text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          {t("common.edit", "Editează")}
                        </button>
                        <button
                          onClick={() => handleDelete(g)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          {t("common.delete", "Șterge")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                      {t("ageGroups.empty", "Nu există grupe de vârstă definite")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={closeModal}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                {editing ? t("ageGroups.edit_title", "Editează grupă") : t("ageGroups.add_title", "Adaugă grupă")}
              </h3>
              <button onClick={closeModal} className="p-2 rounded-lg hover:bg-slate-100">
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("ageGroups.field_name", "Nume")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="U-12, Preteen, Seniori..."
                  className="w-full px-4 py-3 border rounded-xl border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">{t("ageGroups.field_min", "Vârstă min")}</label>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={form.min_age}
                    onChange={(e) => setForm({ ...form, min_age: e.target.value })}
                    placeholder={t("ageGroups.placeholder_blank", "gol = fără minim")}
                    className="w-full px-4 py-3 border rounded-xl border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">{t("ageGroups.field_max", "Vârstă max")}</label>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={form.max_age}
                    onChange={(e) => setForm({ ...form, max_age: e.target.value })}
                    placeholder={t("ageGroups.placeholder_blank", "gol = fără maxim")}
                    className="w-full px-4 py-3 border rounded-xl border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-500">
                {t("ageGroups.bounds_hint", "U-10 ⇒ min gol, max 9. Preteen 12-16 ⇒ min 12, max 16. Seniori ⇒ min 18, max gol. Dacă max e sub 14, datele părintelui devin obligatorii.")}
              </p>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("ageGroups.field_order", "Ordine afișare")}</label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                  placeholder={t("ageGroups.placeholder_order", "ex: 10, 20, 30...")}
                  className="w-full px-4 py-3 border rounded-xl border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">
                  {t("ageGroups.field_difficulty", "Dificultate quiz recomandată")}
                </label>
                <select
                  value={form.default_difficulty}
                  onChange={(e) =>
                    setForm({ ...form, default_difficulty: e.target.value as FormState["default_difficulty"] })
                  }
                  className="w-full px-4 py-3 border rounded-xl border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">{t("ageGroups.diff_none", "Nesetat")}</option>
                  <option value="easy">{t("ageGroups.diff_easy", "Ușor")}</option>
                  <option value="medium">{t("ageGroups.diff_medium", "Mediu")}</option>
                  <option value="hard">{t("ageGroups.diff_hard", "Greu")}</option>
                </select>
                <p className="mt-1.5 text-xs text-slate-500">
                  {t(
                    "ageGroups.difficulty_hint",
                    "Se folosește ca dificultate implicită la crearea quiz-urilor pentru echipele din această grupă."
                  )}
                </p>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700">{t("ageGroups.field_active", "Activă (disponibilă cluburilor)")}</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 font-medium transition-colors bg-slate-100 rounded-xl text-slate-700 hover:bg-slate-200"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 font-medium text-white transition-colors bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? t("common.saving", "Se salvează...") : t("common.save", "Salvează")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
